import React, { useState, useEffect } from 'react'
import {
  Building2,
  AlertTriangle,
  Clock,
  Calendar,
  TrendingUp,
  CheckCircle,
  XCircle,
  Shield,
  BarChart3,
  Users,
  Filter
} from 'lucide-react'
import { fetchWithAuth } from '../lib/api'

interface DashboardMetrics {
  totalVendors: number
  totalAssessments: number
  byStatus: {
    autoApproved: number
    managerApproval: number
    securityReview: number
  }
  contractsExpiringSoon: number // Within 90 days
  pendingAssessments: number
  assessmentsByMonth: {
    month: string
    count: number
  }[]
  totalSpend: number
  averageContractValue: number
  spendByYear: {
    year: string
    spend: number
  }[]
  vendorsByYear: {
    year: string
    count: number
  }[]
  spendPerVendor: number
  spendPerEmployeeByYear: {
    year: string
    spendPerEmployee: number
  }[]
  employeeCount?: number
}

interface TPRMDashboardProps {
  refreshTrigger?: number // Optional prop to force refresh
}

const TPRMDashboard: React.FC<TPRMDashboardProps> = ({ refreshTrigger }) => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)
  const [loading, setLoading] = useState(true)
  const [employeeCount, setEmployeeCount] = useState<number>(1)
  const [excludedVendors, setExcludedVendors] = useState<Set<string>>(new Set())
  const [availableVendors, setAvailableVendors] = useState<string[]>([])
  const [showVendorFilter, setShowVendorFilter] = useState(false)

  useEffect(() => {
    let active = true

    const loadData = async () => {
      try {
        // Load settings to get employee count
        try {
          const settingsRes = await fetchWithAuth('/api/tprm/contracts/settings', {
            credentials: 'include'
          })
          if (settingsRes.ok && active) {
            const settings = await settingsRes.json()
            setEmployeeCount(settings.employeeCount || 1)
            console.log(`📊 Loaded employee count: ${settings.employeeCount || 1}`)
          }
        } catch (settingsError) {
          console.warn('Failed to load settings:', settingsError)
        }

        // Try analytics first (best case - has all metrics)
        let analytics: any = null
        try {
          const analyticsRes = await fetchWithAuth('/api/tprm/contracts/analytics', {
            credentials: 'include'
          })
          if (analyticsRes.ok) {
            analytics = await analyticsRes.json()
            console.log('📊 Using analytics data for dashboard')
          }
        } catch (analyticsError) {
          console.warn('Analytics endpoint failed, will load contracts directly:', analyticsError)
        }

        // Load ALL contracts for accurate dashboard metrics
        // Note: If this times out, we'll need to implement pagination or use analytics endpoint
        let contractsData: any = {}
        let contracts: any[] = []

        try {
          const contractsRes = await fetchWithAuth('/api/tprm/contracts', {
            credentials: 'include'
          })

          if (!contractsRes.ok) {
            throw new Error(`Contracts API returned ${contractsRes.status}`)
          }

          contractsData = await contractsRes.json()
          contracts = contractsData.contracts || []

          console.log(`📊 Loaded ${contracts.length} of ${contractsData.total || '?'} total contracts`)
        } catch (contractsError) {
          console.error('Failed to load contracts:', contractsError)
          // If we can't load contracts, show error state
          throw contractsError
        }

        if (!active) return

        // Filter to only active contracts (exclude archived and duplicates)
        const activeContracts = contracts.filter((c: any) => !c.isArchived && !c.isDuplicate)

        // Further filter to only include contracts that expire today or later
        // This matches the "From today onwards" filter in the Processing tab
        const today = new Date()
        today.setHours(0, 0, 0, 0) // Start of today
        const activeNonExpiredContracts = activeContracts.filter((c: any) => {
          // Check renewal date
          if (c.renewalDate) {
            const renewalDate = new Date(c.renewalDate)
            if (renewalDate >= today) return true
          }

          // Check expiration date
          if (c.expirationDate) {
            const expirationDate = new Date(c.expirationDate)
            if (expirationDate >= today) return true
          }

          // Check effective/contract date
          const effectiveDate = c.extractedMetadata?.dates?.effectiveDate?.value
          if (effectiveDate) {
            const contractDate = new Date(effectiveDate)
            if (contractDate >= today) return true
          }

          // If none of the date checks passed, exclude this contract
          return false
        })

        console.log(`📊 Filtered to ${activeNonExpiredContracts.length} non-expired contracts (from ${activeContracts.length} active contracts)`)

        // Calculate metrics from contracts
        // Group contracts by vendor to get unique vendor count (only non-expired)
        const uniqueVendors = new Set(activeNonExpiredContracts.filter((c: any) => c.vendorName).map((c: any) => c.vendorName))

        // Extract all available vendors for filtering UI
        const vendorList = Array.from(uniqueVendors).sort()
        if (active) {
          setAvailableVendors(vendorList)
        }

        // Calculate contracts with DPA (security/privacy indicator)
        const contractsWithDPA = activeNonExpiredContracts.filter((c: any) => c.hasDpa).length
        const contractsWithoutDPA = activeNonExpiredContracts.filter((c: any) =>
          c.extractionStatus === 'processed' && !c.hasDpa
        ).length

        // Extract processed contracts for categorization
        const processedContracts = activeNonExpiredContracts.filter((c: any) =>
          c.extractionStatus === 'processed' || c.extractionStatus === 'completed'
        )

        // Categorize by processing status
        const byStatus = {
          autoApproved: processedContracts.filter((c: any) =>
            c.extractionStatus === 'processed' && !c.requiresReview
          ).length,
          managerApproval: activeNonExpiredContracts.filter((c: any) =>
            c.extractionStatus === 'processing' || c.requiresReview
          ).length,
          securityReview: contractsWithoutDPA // Contracts without DPA need security review
        }

        // Calculate spend metrics (only from non-expired contracts)
        let totalSpend = 0
        let averageContractValue = 0
        let contractsExpiringSoon = 0

        // Always calculate client-side from non-expired contracts for accuracy
        console.log('📊 Calculating metrics client-side from non-expired contracts')

        // Calculate total spend from non-expired contracts only
        const contractsWithValues = activeNonExpiredContracts.filter((c: any) => c.contractValue && c.contractValue > 0)
        totalSpend = contractsWithValues.reduce((sum: number, c: any) => sum + (c.contractValue || 0), 0)
        averageContractValue = contractsWithValues.length > 0 ? totalSpend / contractsWithValues.length : 0

        // Calculate contracts expiring within 90 days (must be non-expired to begin with)
        const in90Days = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000)
        contractsExpiringSoon = activeNonExpiredContracts.filter((c: any) => {
          const expiryDate = c.expirationDate || c.renewalDate || c.endDate
          if (!expiryDate) return false
          const expiry = new Date(expiryDate)
          return expiry >= today && expiry <= in90Days
        }).length

        const pendingExtractions = contracts.filter((c: any) =>
          c.extractionStatus === 'pending' || c.extractionStatus === 'processing'
        ).length

        // Calculate all time-based charts client-side from non-expired contracts
        // This ensures dashboard only reflects contracts that are currently valid
        let contractsByMonth, spendByYear, vendorsByYear, spendPerEmployeeByYear

        console.log('📊 Calculating charts client-side from non-expired contracts')
        contractsByMonth = getContractsByMonth(activeNonExpiredContracts)
        spendByYear = calculateSpendByYear(activeNonExpiredContracts)
        vendorsByYear = calculateVendorsByYear(activeNonExpiredContracts)
        spendPerEmployeeByYear = calculateSpendPerEmployee(activeNonExpiredContracts, employeeCount, excludedVendors)

        // Use client-calculated unique vendors from non-expired contracts
        const totalVendorsCount = uniqueVendors.size

        // Calculate spend per vendor
        const spendPerVendor = totalVendorsCount > 0 ? totalSpend / totalVendorsCount : 0

        // Use count of non-expired contracts for accurate total
        const totalContractsFromAPI = activeNonExpiredContracts.length

        if (active) {
          setMetrics({
            totalVendors: totalVendorsCount,
            totalAssessments: totalContractsFromAPI, // Use total from API metadata
            byStatus,
            contractsExpiringSoon,
            pendingAssessments: pendingExtractions,
            assessmentsByMonth: contractsByMonth,
            totalSpend,
            averageContractValue,
            spendByYear,
            vendorsByYear,
            spendPerVendor,
            spendPerEmployeeByYear,
            employeeCount: analytics?.employeeCount || employeeCount
          })
        }
      } catch (error) {
        if (active) {
          console.error('Failed to load TPRM metrics:', error)
        }
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }

    loadData()
    return () => { active = false }
  }, [refreshTrigger]) // Reload when refreshTrigger changes

  // Recalculate spend per employee when excluded vendors change
  useEffect(() => {
    let active = true

    if (metrics) {
      // Recalculate without reloading all data
      const spendPerEmployeeByYear = calculateSpendPerEmployee(
        [], // We don't have the contracts array here, will be empty initially
        employeeCount,
        excludedVendors
      )
      // We can't easily update just this metric without the full contract data
      // So we'll just trigger a full reload
      const reloadData = async () => {
        if (active) {
          await loadMetrics()
        }
      }
      reloadData()
    }

    return () => { active = false }
  }, [excludedVendors])

  const loadMetrics = async () => {
    try {
      // Load settings to get employee count
      try {
        const settingsRes = await fetchWithAuth('/api/tprm/contracts/settings', {
          credentials: 'include'
        })
        if (settingsRes.ok) {
          const settings = await settingsRes.json()
          setEmployeeCount(settings.employeeCount || 1)
          console.log(`📊 Loaded employee count: ${settings.employeeCount || 1}`)
        }
      } catch (settingsError) {
        console.warn('Failed to load settings:', settingsError)
      }

      // Try analytics first (best case - has all metrics)
      let analytics: any = null
      try {
        const analyticsRes = await fetchWithAuth('/api/tprm/contracts/analytics', {
          credentials: 'include'
        })
        if (analyticsRes.ok) {
          analytics = await analyticsRes.json()
          console.log('📊 Using analytics data for dashboard')
        }
      } catch (analyticsError) {
        console.warn('Analytics endpoint failed, will load contracts directly:', analyticsError)
      }

      // Load ALL contracts for accurate dashboard metrics
      // Note: If this times out, we'll need to implement pagination or use analytics endpoint
      let contractsData: any = {}
      let contracts: any[] = []

      try {
        const contractsRes = await fetchWithAuth('/api/tprm/contracts', {
          credentials: 'include'
        })

        if (!contractsRes.ok) {
          throw new Error(`Contracts API returned ${contractsRes.status}`)
        }

        contractsData = await contractsRes.json()
        contracts = contractsData.contracts || []

        console.log(`📊 Loaded ${contracts.length} of ${contractsData.total || '?'} total contracts`)
      } catch (contractsError) {
        console.error('Failed to load contracts:', contractsError)
        // If we can't load contracts, show error state
        throw contractsError
      }

      // Filter to only active contracts (exclude archived and duplicates)
      const activeContracts = contracts.filter((c: any) => !c.isArchived && !c.isDuplicate)

      // Further filter to only include contracts that expire today or later
      // This matches the "From today onwards" filter in the Processing tab
      const today = new Date()
      today.setHours(0, 0, 0, 0) // Start of today
      const activeNonExpiredContracts = activeContracts.filter((c: any) => {
        // Check renewal date
        if (c.renewalDate) {
          const renewalDate = new Date(c.renewalDate)
          if (renewalDate >= today) return true
        }

        // Check expiration date
        if (c.expirationDate) {
          const expirationDate = new Date(c.expirationDate)
          if (expirationDate >= today) return true
        }

        // Check effective/contract date
        const effectiveDate = c.extractedMetadata?.dates?.effectiveDate?.value
        if (effectiveDate) {
          const contractDate = new Date(effectiveDate)
          if (contractDate >= today) return true
        }

        // If none of the date checks passed, exclude this contract
        return false
      })

      console.log(`📊 Filtered to ${activeNonExpiredContracts.length} non-expired contracts (from ${activeContracts.length} active contracts)`)

      // Calculate metrics from contracts
      // Group contracts by vendor to get unique vendor count (only non-expired)
      const uniqueVendors = new Set(activeNonExpiredContracts.filter((c: any) => c.vendorName).map((c: any) => c.vendorName))

      // Extract all available vendors for filtering UI
      const vendorList = Array.from(uniqueVendors).sort()
      setAvailableVendors(vendorList)

      // Calculate contracts with DPA (security/privacy indicator)
      const contractsWithDPA = activeNonExpiredContracts.filter((c: any) => c.hasDpa).length
      const contractsWithoutDPA = activeNonExpiredContracts.filter((c: any) =>
        c.extractionStatus === 'processed' && !c.hasDpa
      ).length

      // Extract processed contracts for categorization
      const processedContracts = activeNonExpiredContracts.filter((c: any) =>
        c.extractionStatus === 'processed' || c.extractionStatus === 'completed'
      )

      // Categorize by processing status
      const byStatus = {
        autoApproved: processedContracts.filter((c: any) =>
          c.extractionStatus === 'processed' && !c.requiresReview
        ).length,
        managerApproval: activeNonExpiredContracts.filter((c: any) =>
          c.extractionStatus === 'processing' || c.requiresReview
        ).length,
        securityReview: contractsWithoutDPA // Contracts without DPA need security review
      }

      // Calculate spend metrics (only from non-expired contracts)
      let totalSpend = 0
      let averageContractValue = 0
      let contractsExpiringSoon = 0

      // Always calculate client-side from non-expired contracts for accuracy
      console.log('📊 Calculating metrics client-side from non-expired contracts')

      // Calculate total spend from non-expired contracts only
      const contractsWithValues = activeNonExpiredContracts.filter((c: any) => c.contractValue && c.contractValue > 0)
      totalSpend = contractsWithValues.reduce((sum: number, c: any) => sum + (c.contractValue || 0), 0)
      averageContractValue = contractsWithValues.length > 0 ? totalSpend / contractsWithValues.length : 0

      // Calculate contracts expiring within 90 days (must be non-expired to begin with)
      const in90Days = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000)
      contractsExpiringSoon = activeNonExpiredContracts.filter((c: any) => {
        const expiryDate = c.expirationDate || c.renewalDate || c.endDate
        if (!expiryDate) return false
        const expiry = new Date(expiryDate)
        return expiry >= today && expiry <= in90Days
      }).length

      const pendingExtractions = contracts.filter((c: any) =>
        c.extractionStatus === 'pending' || c.extractionStatus === 'processing'
      ).length

      // Calculate all time-based charts client-side from non-expired contracts
      // This ensures dashboard only reflects contracts that are currently valid
      let contractsByMonth, spendByYear, vendorsByYear, spendPerEmployeeByYear

      console.log('📊 Calculating charts client-side from non-expired contracts')
      contractsByMonth = getContractsByMonth(activeNonExpiredContracts)
      spendByYear = calculateSpendByYear(activeNonExpiredContracts)
      vendorsByYear = calculateVendorsByYear(activeNonExpiredContracts)
      spendPerEmployeeByYear = calculateSpendPerEmployee(activeNonExpiredContracts, employeeCount, excludedVendors)

      // Use client-calculated unique vendors from non-expired contracts
      const totalVendorsCount = uniqueVendors.size

      // Calculate spend per vendor
      const spendPerVendor = totalVendorsCount > 0 ? totalSpend / totalVendorsCount : 0

      // Use count of non-expired contracts for accurate total
      const totalContractsFromAPI = activeNonExpiredContracts.length

      setMetrics({
        totalVendors: totalVendorsCount,
        totalAssessments: totalContractsFromAPI, // Use total from API metadata
        byStatus,
        contractsExpiringSoon,
        pendingAssessments: pendingExtractions,
        assessmentsByMonth: contractsByMonth,
        totalSpend,
        averageContractValue,
        spendByYear,
        vendorsByYear,
        spendPerVendor,
        spendPerEmployeeByYear,
        employeeCount: analytics?.employeeCount || employeeCount
      })
    } catch (error) {
      console.error('Failed to load TPRM metrics:', error)
    } finally {
      setLoading(false)
    }
  }

  const getContractsByMonth = (contracts: any[]) => {
    console.log(`📊 getContractsByMonth called with ${contracts.length} contracts`)

    const monthCounts: { [key: string]: number } = {}
    const months = []

    // Last 6 months
    for (let i = 5; i >= 0; i--) {
      const date = new Date()
      date.setMonth(date.getMonth() - i)
      const monthKey = date.toISOString().substring(0, 7) // YYYY-MM
      monthCounts[monthKey] = 0
      months.push({
        month: date.toLocaleDateString('en-US', { month: 'short' }),
        monthKey
      })
    }

    // Count contracts by month (by upload date)
    let matchedCount = 0
    let noDateCount = 0
    contracts.forEach((contract: any) => {
      if (!contract.uploadedAt && !contract.createdAt) {
        noDateCount++
        return
      }
      const uploadDate = new Date(contract.uploadedAt || contract.createdAt)
      const monthKey = uploadDate.toISOString().substring(0, 7)
      if (monthCounts[monthKey] !== undefined) {
        monthCounts[monthKey]++
        matchedCount++
      }
    })

    console.log(`📊 Upload trends: matched ${matchedCount} of ${contracts.length} contracts in 6-month window`)
    console.log(`📊 Contracts without dates: ${noDateCount}`)
    console.log(`📊 Month counts:`, monthCounts)

    return months.map(m => ({
      month: m.month,
      count: monthCounts[m.monthKey]
    }))
  }

  const calculateSpendByYear = (contracts: any[]) => {
    const spendByYear: { [year: string]: number } = {}

    // Get last 3 years for YoY comparison
    const currentYear = new Date().getFullYear()
    for (let i = 2; i >= 0; i--) {
      const year = (currentYear - i).toString()
      spendByYear[year] = 0
    }

    // Aggregate spend by contract year (use effectiveDate if available, otherwise fall back to uploadedAt)
    contracts.forEach((contract: any) => {
      if (contract.contractValue) {
        // Use effectiveDate if available, otherwise fall back to uploadedAt
        const dateStr = contract.effectiveDate || contract.uploadedAt
        if (dateStr) {
          const year = new Date(dateStr).getFullYear().toString()
          if (spendByYear[year] !== undefined) {
            spendByYear[year] += contract.contractValue
          }
        }
      }
    })

    return Object.entries(spendByYear).map(([year, spend]) => ({
      year,
      spend
    }))
  }

  const calculateVendorsByYear = (contracts: any[]) => {
    const vendorsByYear: { [year: string]: Set<string> } = {}

    // Get last 3 years
    const currentYear = new Date().getFullYear()
    for (let i = 2; i >= 0; i--) {
      const year = (currentYear - i).toString()
      vendorsByYear[year] = new Set()
    }

    // Track unique vendors by contract year (use effectiveDate if available, otherwise fall back to uploadedAt)
    contracts.forEach((contract: any) => {
      if (contract.vendorName) {
        // Use effectiveDate if available, otherwise fall back to uploadedAt
        const dateStr = contract.effectiveDate || contract.uploadedAt
        if (dateStr) {
          const year = new Date(dateStr).getFullYear().toString()
          if (vendorsByYear[year] !== undefined) {
            vendorsByYear[year].add(contract.vendorName)
          }
        }
      }
    })

    return Object.entries(vendorsByYear).map(([year, vendors]) => ({
      year,
      count: vendors.size
    }))
  }

  const calculateSpendPerEmployee = (contracts: any[], employeeCount: number, excludedVendors: Set<string>) => {
    const spendByYear: { [year: string]: number } = {}

    // Get last 3 years for YoY comparison
    const currentYear = new Date().getFullYear()
    for (let i = 2; i >= 0; i--) {
      const year = (currentYear - i).toString()
      spendByYear[year] = 0
    }

    // Filter contracts to exclude specified vendors AND only include IT spend
    const filteredContracts = contracts.filter((contract: any) =>
      !excludedVendors.has(contract.vendorName) && contract.isITSpend
    )

    // Aggregate IT spend by contract year (use effectiveDate if available, otherwise fall back to uploadedAt)
    filteredContracts.forEach((contract: any) => {
      if (contract.contractValue) {
        // Use effectiveDate if available, otherwise fall back to uploadedAt
        const dateStr = contract.effectiveDate || contract.uploadedAt
        if (dateStr) {
          const year = new Date(dateStr).getFullYear().toString()
          if (spendByYear[year] !== undefined) {
            spendByYear[year] += contract.contractValue
          }
        }
      }
    })

    // Calculate spend per employee (ensure minimum of 1 employee)
    const actualEmployeeCount = Math.max(employeeCount, 1)
    return Object.entries(spendByYear).map(([year, spend]) => ({
      year,
      spendPerEmployee: Math.round(spend / actualEmployeeCount)
    }))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading dashboard...</div>
      </div>
    )
  }

  if (!metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Failed to load dashboard metrics</div>
      </div>
    )
  }

  const totalStatus = metrics.byStatus.autoApproved + metrics.byStatus.managerApproval +
                      metrics.byStatus.securityReview

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Contract Management Dashboard</h2>
        <p className="text-sm text-gray-600 mt-1">
          Overview of vendor contracts, spend analysis, and risk metrics
        </p>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          title="Total Vendors"
          value={metrics.totalVendors}
          icon={<Building2 className="h-5 w-5" />}
          description={`${metrics.totalAssessments} contracts`}
        />
        <MetricCard
          title="Total Spend"
          value={`$${(metrics.totalSpend / 1000000).toFixed(1)}M`}
          icon={<TrendingUp className="h-5 w-5" />}
          className="border-green-200 bg-green-50"
          valueClassName="text-green-600"
          description={`Avg: $${Math.round(metrics.averageContractValue / 1000)}K`}
          isFormatted
        />
        <MetricCard
          title="Spend per Vendor"
          value={
            metrics.spendPerVendor >= 1000000
              ? `$${(metrics.spendPerVendor / 1000000).toFixed(2)}M`
              : metrics.spendPerVendor >= 1000
                ? `$${(metrics.spendPerVendor / 1000).toFixed(1)}K`
                : `$${Math.round(metrics.spendPerVendor)}`
          }
          icon={<TrendingUp className="h-5 w-5" />}
          className="border-blue-200 bg-blue-50"
          valueClassName="text-blue-600"
          description="Average per vendor"
          isFormatted
        />
        <MetricCard
          title="Contracts Expiring"
          value={metrics.contractsExpiringSoon}
          icon={<Calendar className="h-5 w-5" />}
          className={metrics.contractsExpiringSoon > 0 ? 'border-yellow-200 bg-yellow-50' : ''}
          valueClassName={metrics.contractsExpiringSoon > 0 ? 'text-yellow-600' : 'text-gray-900'}
          description="Within 90 days"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6">
        {/* Contract Processing Status */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold">Contract Status</h3>
          </div>

          {totalStatus > 0 ? (
            <div className="space-y-4">
              <BarItem
                label="Processed"
                count={metrics.byStatus.autoApproved}
                total={totalStatus}
                color="bg-green-500"
                icon={<CheckCircle className="h-4 w-4" />}
              />
              <BarItem
                label="In Progress"
                count={metrics.byStatus.managerApproval}
                total={totalStatus}
                color="bg-yellow-500"
                icon={<Clock className="h-4 w-4" />}
              />
              <BarItem
                label="Needs Review"
                count={metrics.byStatus.securityReview}
                total={totalStatus}
                color="bg-red-500"
                icon={<AlertTriangle className="h-4 w-4" />}
              />
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No contracts yet
            </div>
          )}
        </div>
      </div>

      {/* Trending Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* YoY Spend Trend */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold">Year-over-Year Spend</h3>
            <span className="text-sm text-gray-500 ml-auto">Last 3 years</span>
          </div>

          {metrics.spendByYear.some(y => y.spend > 0) ? (
            <div className="space-y-2">
              <svg viewBox="0 0 400 200" className="w-full h-48">
                {/* Grid lines */}
                {[0, 0.25, 0.5, 0.75, 1].map((percent, i) => (
                  <g key={i}>
                    <line
                      x1="40" y1={180 - percent * 160}
                      x2="390" y2={180 - percent * 160}
                      stroke="#e5e7eb" strokeWidth="1"
                    />
                    <text x="5" y={185 - percent * 160} fontSize="10" fill="#6b7280">
                      ${((Math.max(...metrics.spendByYear.map(y => y.spend)) * percent) / 1000000).toFixed(1)}M
                    </text>
                  </g>
                ))}

                {/* Line chart */}
                <polyline
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="3"
                  points={metrics.spendByYear.map((item, idx) => {
                    const maxSpend = Math.max(...metrics.spendByYear.map(y => y.spend), 1)
                    const x = 60 + (idx * (300 / (metrics.spendByYear.length - 1 || 1)))
                    const y = 180 - (item.spend / maxSpend) * 160
                    return `${x},${y}`
                  }).join(' ')}
                />

                {/* Data points */}
                {metrics.spendByYear.map((item, idx) => {
                  const maxSpend = Math.max(...metrics.spendByYear.map(y => y.spend), 1)
                  const x = 60 + (idx * (300 / (metrics.spendByYear.length - 1 || 1)))
                  const y = 180 - (item.spend / maxSpend) * 160
                  return (
                    <g key={idx}>
                      <circle cx={x} cy={y} r="4" fill="#10b981" />
                      <text x={x} y={y - 10} fontSize="10" fill="#374151" textAnchor="middle">
                        ${(item.spend / 1000000).toFixed(1)}M
                      </text>
                      <text x={x} y={195} fontSize="11" fill="#6b7280" textAnchor="middle">
                        {item.year}
                      </text>
                    </g>
                  )
                })}
              </svg>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No spend data available
            </div>
          )}
        </div>

        {/* Vendor Growth */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold">Vendor Growth</h3>
            <span className="text-sm text-gray-500 ml-auto">Last 3 years</span>
          </div>

          {metrics.vendorsByYear.some(y => y.count > 0) ? (
            <div className="space-y-2">
              <svg viewBox="0 0 400 200" className="w-full h-48">
                {/* Grid lines */}
                {[0, 0.25, 0.5, 0.75, 1].map((percent, i) => (
                  <g key={i}>
                    <line
                      x1="40" y1={180 - percent * 160}
                      x2="390" y2={180 - percent * 160}
                      stroke="#e5e7eb" strokeWidth="1"
                    />
                    <text x="5" y={185 - percent * 160} fontSize="10" fill="#6b7280">
                      {Math.round(Math.max(...metrics.vendorsByYear.map(y => y.count)) * percent)}
                    </text>
                  </g>
                ))}

                {/* Line chart */}
                <polyline
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="3"
                  points={metrics.vendorsByYear.map((item, idx) => {
                    const maxCount = Math.max(...metrics.vendorsByYear.map(y => y.count), 1)
                    const x = 60 + (idx * (300 / (metrics.vendorsByYear.length - 1 || 1)))
                    const y = 180 - (item.count / maxCount) * 160
                    return `${x},${y}`
                  }).join(' ')}
                />

                {/* Data points */}
                {metrics.vendorsByYear.map((item, idx) => {
                  const maxCount = Math.max(...metrics.vendorsByYear.map(y => y.count), 1)
                  const x = 60 + (idx * (300 / (metrics.vendorsByYear.length - 1 || 1)))
                  const y = 180 - (item.count / maxCount) * 160
                  return (
                    <g key={idx}>
                      <circle cx={x} cy={y} r="4" fill="#3b82f6" />
                      <text x={x} y={y - 10} fontSize="10" fill="#374151" textAnchor="middle">
                        {item.count}
                      </text>
                      <text x={x} y={195} fontSize="11" fill="#6b7280" textAnchor="middle">
                        {item.year}
                      </text>
                    </g>
                  )
                })}
              </svg>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No vendor growth data available
            </div>
          )}
        </div>

        {/* Spend per Employee */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold">Spend per Employee</h3>
            <span className="text-sm text-gray-500 ml-auto">Last 3 years</span>
          </div>

          {metrics.employeeCount && metrics.employeeCount >= 1 && metrics.spendPerEmployeeByYear.length > 0 ? (
            <div className="space-y-2">
              <svg viewBox="0 0 400 200" className="w-full h-48">
                {/* Grid lines */}
                {[0, 0.25, 0.5, 0.75, 1].map((percent, i) => (
                  <g key={i}>
                    <line
                      x1="40" y1={180 - percent * 160}
                      x2="390" y2={180 - percent * 160}
                      stroke="#e5e7eb" strokeWidth="1"
                    />
                    <text x="5" y={185 - percent * 160} fontSize="10" fill="#6b7280">
                      ${Math.round(Math.max(...metrics.spendPerEmployeeByYear.map(y => y.spendPerEmployee)) * percent)}
                    </text>
                  </g>
                ))}

                {/* Line chart */}
                <polyline
                  fill="none"
                  stroke="#a855f7"
                  strokeWidth="3"
                  points={metrics.spendPerEmployeeByYear.map((item, idx) => {
                    const maxSpend = Math.max(...metrics.spendPerEmployeeByYear.map(y => y.spendPerEmployee), 1)
                    const x = 60 + (idx * (300 / (metrics.spendPerEmployeeByYear.length - 1 || 1)))
                    const y = 180 - (item.spendPerEmployee / maxSpend) * 160
                    return `${x},${y}`
                  }).join(' ')}
                />

                {/* Data points */}
                {metrics.spendPerEmployeeByYear.map((item, idx) => {
                  const maxSpend = Math.max(...metrics.spendPerEmployeeByYear.map(y => y.spendPerEmployee), 1)
                  const x = 60 + (idx * (300 / (metrics.spendPerEmployeeByYear.length - 1 || 1)))
                  const y = 180 - (item.spendPerEmployee / maxSpend) * 160
                  return (
                    <g key={idx}>
                      <circle cx={x} cy={y} r="4" fill="#a855f7" />
                      <text x={x} y={y - 10} fontSize="10" fill="#374151" textAnchor="middle">
                        ${Math.round(item.spendPerEmployee)}
                      </text>
                      <text x={x} y={195} fontSize="11" fill="#6b7280" textAnchor="middle">
                        {item.year}
                      </text>
                    </g>
                  )
                })}
              </svg>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              {!metrics.employeeCount || metrics.employeeCount < 1 ? 'Set employee count in settings' : 'No spend data available'}
            </div>
          )}
        </div>

      </div>
    </div>
  )
}

const MetricCard: React.FC<{
  title: string
  value: number | string
  icon: React.ReactNode
  description?: string
  className?: string
  valueClassName?: string
  isFormatted?: boolean
}> = ({ title, value, icon, description, className = '', valueClassName = 'text-gray-900', isFormatted = false }) => (
  <div className={`bg-white border border-gray-200 rounded-lg p-6 ${className}`}>
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <p className="text-sm text-gray-600">{title}</p>
        <p className={`text-3xl font-bold mt-2 ${valueClassName}`}>{value}</p>
        {description && (
          <p className="text-xs text-gray-500 mt-1">{description}</p>
        )}
      </div>
      <div className="text-gray-400">{icon}</div>
    </div>
  </div>
)

const BarItem: React.FC<{
  label: string
  count: number
  total: number
  color: string
  icon?: React.ReactNode
}> = ({ label, count, total, color, icon }) => {
  const percent = total > 0 ? (count / total) * 100 : 0

  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <div className="flex items-center gap-2">
          {icon && <span className="text-gray-500">{icon}</span>}
          <span className="font-medium text-gray-700">{label}</span>
        </div>
        <span className="text-gray-600">
          {count} ({Math.round(percent)}%)
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`${color} h-2 rounded-full transition-all`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  )
}

export default TPRMDashboard
